import {
  generatedApi,
  type WidgetSettings,
  type WidgetSettingsListItem,
} from '@shared/api/generated-api';
import type { RootState } from '@shared/redux';

const toListItem = (preset: WidgetSettings): WidgetSettingsListItem => ({
  id: preset.id,
  name: preset.name,
  description: preset.description,
  created_at: preset.created_at,
  updated_at: preset.updated_at,
});

export const presetsEndpoints = {
  getWidgetSettingsListWidgetSettingsListGet: {
    keepUnusedDataFor: 600,
    providesTags: [{ type: 'widget_settings' as const, id: 'LIST' }],
  },

  getWidgetSettingsInfoWidgetSettingsInfoGet: {
    keepUnusedDataFor: 600,
    providesTags: (_result, _error, arg) => [
      { type: 'widget_settings' as const, id: arg.widgetSettingsId },
    ],
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      try {
        const { data } = await queryFulfilled;
        const cachedListArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getWidgetSettingsListWidgetSettingsListGet',
        );

        for (const queryArg of cachedListArgs) {
          dispatch(
            generatedApi.util.updateQueryData(
              'getWidgetSettingsListWidgetSettingsListGet',
              queryArg,
              (draft) => {
                const index = draft.findIndex((preset) => preset.id === arg.widgetSettingsId);
                if (index !== -1) draft[index] = toListItem(data);
              },
            ),
          );
        }
      } catch {}
    },
  },

  createWidgetSettingsWidgetSettingsCreatePost: {
    invalidatesTags: [],
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      try {
        const { data } = await queryFulfilled;

        dispatch(
          generatedApi.util.upsertQueryData(
            'getWidgetSettingsInfoWidgetSettingsInfoGet',
            { widgetSettingsId: data.id },
            data,
          ),
        );

        const cachedListArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getWidgetSettingsListWidgetSettingsListGet',
        );

        for (const queryArg of cachedListArgs) {
          if (queryArg.organizationId !== arg.organizationId) continue;

          dispatch(
            generatedApi.util.updateQueryData(
              'getWidgetSettingsListWidgetSettingsListGet',
              queryArg,
              (draft) => {
                const preset = toListItem(data);
                const index = draft.findIndex((item) => item.id === data.id);

                if (index === -1) draft.unshift(preset);
                else draft[index] = preset;
              },
            ),
          );
        }
      } catch {}
    },
  },

  updateWidgetSettingsWidgetSettingsUpdatePost: {
    invalidatesTags: [],
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      try {
        const { data } = await queryFulfilled;

        dispatch(
          generatedApi.util.upsertQueryData(
            'getWidgetSettingsInfoWidgetSettingsInfoGet',
            { widgetSettingsId: arg.widgetSettingsId },
            data,
          ),
        );

        const cachedListArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getWidgetSettingsListWidgetSettingsListGet',
        );

        for (const queryArg of cachedListArgs) {
          dispatch(
            generatedApi.util.updateQueryData(
              'getWidgetSettingsListWidgetSettingsListGet',
              queryArg,
              (draft) => {
                const index = draft.findIndex((preset) => preset.id === arg.widgetSettingsId);
                if (index !== -1) draft[index] = toListItem(data);
              },
            ),
          );
        }
      } catch {}
    },
  },

  deleteWidgetSettingsWidgetSettingsDeleteDelete: {
    invalidatesTags: (_result, _error, arg) => [
      { type: 'widget_settings' as const, id: arg.widgetSettingsId },
    ],
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      try {
        await queryFulfilled;
        const cachedListArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getWidgetSettingsListWidgetSettingsListGet',
        );

        for (const queryArg of cachedListArgs) {
          dispatch(
            generatedApi.util.updateQueryData(
              'getWidgetSettingsListWidgetSettingsListGet',
              queryArg,
              (draft) => {
                const index = draft.findIndex((preset) => preset.id === arg.widgetSettingsId);
                if (index !== -1) draft.splice(index, 1);
              },
            ),
          );
        }
      } catch {}
    },
  },
} satisfies Parameters<typeof generatedApi.enhanceEndpoints>[0]['endpoints'];
